import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["items", "value", "position", "getItemAttributes", "getItemDisabled", "getItemKey", "getItemLabel", "getItemOnClick", "getItemRef", "onItemClick", "getItemTagName", "buttonScrollThrough", "style"];
import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { cnBem, combineRefs } from '@carnica/utils';
import styles from "./styles.pcss";
import { TabBarItem } from "../TabBarItem";
import { useTabBar } from "../hooks/useTabBar";
import { TabBarNavWrapper } from "./TabBarNavWrapper";
import { useNavigationScroll } from "../hooks/useNavigationScroll";
import { useHiddenElementsCount } from "../hooks/useHiddenElementsCount";
import { useScrollBlur } from "../hooks/useScrollBlur";
var cnTabBarNav = cnBem('tabbar-nav', styles);
var TabBarNavRender = function TabBarNavRender(props, ref) {
  var _ref;
  var _props$items = props.items,
    items = _props$items === void 0 ? [] : _props$items,
    value = props.value,
    position = props.position,
    getItemAttributes = props.getItemAttributes,
    getItemDisabled = props.getItemDisabled,
    getItemKey = props.getItemKey,
    getItemLabel = props.getItemLabel,
    getItemOnClick = props.getItemOnClick,
    getItemRef = props.getItemRef,
    onItemClick = props.onItemClick,
    getItemTagName = props.getItemTagName,
    _props$buttonScrollTh = props.buttonScrollThrough,
    buttonScrollThrough = _props$buttonScrollTh === void 0 ? 'end' : _props$buttonScrollTh,
    style = props.style,
    otherProps = _objectWithoutProperties(props, _excluded);
  var _useState = useState(-1),
    _useState2 = _slicedToArray(_useState, 2),
    hoveredIndex = _useState2[0],
    setHoveredIndex = _useState2[1];
  var wrapperRef = useRef(null);
  var _useTabBar = useTabBar({
      items: items,
      getItemKey: getItemKey,
      value: value
    }),
    tabBarRefs = _useTabBar.tabBarRefs,
    pointer = _useTabBar.pointer,
    tabWidths = _useTabBar.tabWidths,
    activeIndex = _useTabBar.activeIndex;
  var indexHandler = useCallback(function (index) {
    return function () {
      setHoveredIndex(index);
    };
  }, []);
  var handleItemClick = useCallback(function (item) {
    return function (e) {
      var _getItemOnClick;
      onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick(item, e);
      (_getItemOnClick = getItemOnClick(item)) === null || _getItemOnClick === void 0 ? void 0 : _getItemOnClick(e);
    };
  }, [onItemClick, getItemOnClick]);
  var hiddenCount = useHiddenElementsCount({
    wrapperRef: wrapperRef,
    tabWidths: tabWidths
  });
  var _useScrollBlur = useScrollBlur({
      position: position,
      wrapperRef: wrapperRef,
      tabWidths: tabWidths,
      offset: {
        left: position === 'fixed' ? 6 : 4
      }
    }),
    setBlurOffset = _useScrollBlur.setBlurOffset,
    blurAreaRefs = _useScrollBlur.blurAreaRefs;
  var onScrollButtonClick = useNavigationScroll({
    wrapperRef: wrapperRef,
    buttonScrollThrough: buttonScrollThrough,
    tabWidths: tabWidths,
    position: position,
    setBlurOffset: setBlurOffset,
    activeIndex: activeIndex
  });
  return /*#__PURE__*/React.createElement(TabBarNavWrapper, _extends({
    blurAreaRefs: blurAreaRefs,
    hiddenCount: hiddenCount,
    onScrollButtonClick: onScrollButtonClick,
    onUlMouseLeave: indexHandler(-1),
    position: position,
    ref: ref,
    wrapperRef: wrapperRef
  }, otherProps, {
    "data-t-id": "TabBar-TabBarNav"
  }), /*#__PURE__*/React.createElement("div", {
    className: cnTabBarNav('pointer', {
      visible: !!pointer.width
    }),
    style: (_ref = {}, _defineProperty(_ref, '--tabbar-pointer-width', "".concat(pointer.width, "px")), _defineProperty(_ref, '--tabbar-pointer-offset', "".concat(pointer.offset, "px")), _ref),
    "data-t-id": "TabBar-TabBarNav"
  }), items.map(function (item, i) {
    return /*#__PURE__*/React.createElement("li", {
      className: cnTabBarNav('item'),
      key: cnTabBarNav('item', {
        i: i
      }),
      onMouseEnter: indexHandler(i)
    }, /*#__PURE__*/React.createElement(TabBarItem, _extends({
      accent: hoveredIndex !== -1 ? activeIndex === i || hoveredIndex === i : true,
      disabled: getItemDisabled(item),
      onClick: handleItemClick(item),
      ref: combineRefs([getItemRef(item), tabBarRefs[i]]),
      size: position === 'fixed' ? 's' : 'm',
      tagName: getItemTagName(item)
    }, getItemAttributes(item) || {}), getItemLabel(item)));
  }));
};
export var TabBarNav = /*#__PURE__*/forwardRef(TabBarNavRender);